import { motion } from "framer-motion";
import { lazy, Suspense } from "react";

const HeroScene = lazy(() => import("@/components/HeroScene").then(module => ({ default: module.HeroScene })));

const MaskedLine = ({ children, delay = 0, className = "" }) => (
    <span className={`block overflow-hidden pb-3 -mb-3 ${className}`}>
        <motion.span
            className="block will-change-transform"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const scrollToWork = () => {
        const target = document.querySelector("#projects");
        if (window.__lenis && target) window.__lenis.scrollTo(target, { offset: -60 });
        else target?.scrollIntoView({ behavior: "smooth" });
    };
    return (
        <section id="about" data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
            <Suspense fallback={null}>
                <HeroScene />
            </Suspense>
            <div className="pointer-events-none relative z-10 mx-auto w-full max-w-6xl px-6 pt-28 sm:px-10">
                <MaskedLine delay={0.15}>
                    <span className="font-mono text-xs uppercase tracking-[0.3em] text-coffee">
                        Full Stack Developer — React &amp; Django
                    </span>
                </MaskedLine>
                <h1 className="mt-6 font-serif text-4xl font-black leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-7xl">
                    <MaskedLine delay={0.3}>Hello, I&apos;m Yuvraj.</MaskedLine>
                    <MaskedLine delay={0.45} className="mt-2">
                        <span className="font-serif text-2xl font-semibold italic text-coffee-dark sm:text-3xl lg:text-4xl">
                            I write code, drink coffee, and make computers do things.
                        </span>
                    </MaskedLine>
                </h1>
                <MaskedLine delay={0.65} className="mt-5">
                    <span className="font-mono text-sm italic text-coffee/80">
                        // Sometimes they even do what I want them to.
                    </span>
                </MaskedLine>
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-10"
                >
                    <button
                        data-testid="hero-cta-button"
                        onClick={scrollToWork}
                        className="pointer-events-auto group relative overflow-hidden rounded-full bg-coffee px-8 py-3.5 font-mono text-sm tracking-wide text-paper shadow-[0_10px_30px_-10px_rgba(61,35,20,0.5)] transition-transform duration-300 hover:scale-[1.04] active:scale-95"
                    >
                        <span className="relative z-10">View My Work</span>
                        <span className="absolute inset-0 -translate-x-full bg-coffee-dark transition-transform duration-500 ease-out group-hover:translate-x-0" />
                    </button>
                </motion.div>
            </div>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
            >
                <div className="flex flex-col items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-coffee/70">Scroll</span>
                    <motion.span
                        className="block h-8 w-px bg-coffee/50"
                        animate={{ scaleY: [1, 0.3, 1], opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                    />
                </div>
            </motion.div>
        </section>
    );
};
